import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, EnvelopeSimple, Eye } from "@phosphor-icons/react";
import api, { apiError } from "../../lib/api";
import { INVESTOR_TYPES } from "../../lib/plans";
import { downloadDoc } from "../../lib/download";
import { inr, pct, fmtDate, kycBadge, riskBadge } from "../../lib/format";
import AppLayout, { PageLoader, EmptyState, thCls, tdCls, rowCls } from "../../components/AppLayout";
import StatCard from "../../components/ui/StatCard";
import SecureAvatar from "../../components/SecureAvatar";
import WithdrawalPanel from "../../components/WithdrawalPanel";
import { AllocationDonut } from "../../components/charts";
import { Badge } from "../../components/ui/badge";

const TABS = ["Overview", "KYC", "Documents", "Plan & Risk", "Notes"];

const Field = ({ label, value }) => (
  <div>
    <p className="label-caps">{label}</p>
    <p className="text-sm font-medium text-white mt-1.5">{value || "—"}</p>
  </div>
);

const MaskedField = ({ label, masked, revealed, onReveal, revealing, testId }) => (
  <div>
    <p className="label-caps">{label}</p>
    <div className="flex items-center gap-2 mt-1.5">
      <p className="text-sm font-medium text-white tnum" data-testid={`${testId}-value`}>{revealed || masked || "—"}</p>
      {!revealed && masked && masked !== "—" && (
        <button onClick={onReveal} disabled={revealing} data-testid={`${testId}-reveal`}
          title="Reveal (audit logged)"
          className="h-6 w-6 rounded-md border border-white/10 flex items-center justify-center text-zinc-500 hover:text-emerald-300 hover:border-emerald-500/30 transition-colors duration-150">
          <Eye size={12} />
        </button>
      )}
    </div>
  </div>
);

export default function WrmClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("Overview");
  const [contact, setContact] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const hideTimer = useRef(null);

  useEffect(() => {
    api.get(`/wrm/clients/${id}`).then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
    return () => { if (hideTimer.current) clearTimeout(hideTimer.current); };
  }, [id]);

  const revealContact = async () => {
    setRevealing(true);
    try {
      const r = await api.post(`/wrm/clients/${id}/contact-reveal`);
      setContact(r.data);
      if (hideTimer.current) clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => setContact(null), 60000);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setRevealing(false);
    }
  };

  if (error) {
    return (
      <AppLayout title="Client">
        <div className="glass-card p-8 max-w-lg" data-testid="client-denied">
          <p className="font-heading font-semibold text-white">Client not accessible</p>
          <p className="text-sm text-zinc-500 mt-2">{error}</p>
        </div>
      </AppLayout>
    );
  }
  if (!data) return <AppLayout title="Client"><PageLoader /></AppLayout>;

  const { profile, metrics, allocation, kyc_records, kyc_completion, documents, withdrawal } = data;

  return (
    <AppLayout title={profile.full_name}>
      <div className="fade-up space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button onClick={() => navigate("/wrm/clients")} data-testid="back-to-clients"
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors duration-200">
            <ArrowLeft size={15} /> Back to Clients
          </button>
          <div className="flex items-center gap-2">
            <a href={`mailto:info@kmcsolutions.co.in?subject=${encodeURIComponent(`KMC Solutions — Client Support / Query — ${profile.full_name} (${profile.investor_code})`)}`}
              data-testid="wc-contact-email"
              className="flex items-center gap-2 text-xs font-semibold text-emerald-400 border border-emerald-500/25 rounded-lg px-3.5 py-2 hover:bg-emerald-500/10 transition-colors duration-200">
              <EnvelopeSimple size={14} /> Email
            </a>
          </div>
        </div>

        <div className="glass-card p-6 md:p-7" data-testid="wc-header">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
            <div className="flex items-center gap-4">
              <SecureAvatar investorId={id} name={profile.full_name} size={56} testId="wc-client-photo" />
              <div>
                <h2 className="font-display text-2xl text-white">{profile.full_name}</h2>
                <p className="tnum text-xs text-zinc-500 mt-0.5">{profile.investor_code} · {profile.city}{profile.state ? `, ${profile.state}` : ""}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 ml-auto">
              <div><p className="label-caps">AUM</p><p className="tnum text-lg text-white mt-1">{inr(metrics.current_value)}</p></div>
              <div><p className="label-caps">Risk</p><Badge variant="outline" className={`${riskBadge(profile.risk_profile)} text-[10px] mt-1`}>{profile.risk_profile}</Badge></div>
              <div><p className="label-caps">KYC</p><Badge variant="outline" className={`${kycBadge(profile.kyc_status)} text-[10px] mt-1`} data-testid="wc-kyc-status">{profile.kyc_status}</Badge></div>
            </div>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="wc-tabs">
          {TABS.map((t) => (
            <button key={t} data-testid={`wc-tab-${t.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setTab(t)}
              className={`text-xs font-semibold rounded-full px-4 py-2 border whitespace-nowrap transition-colors duration-200 ${
                tab === t ? "pill-active" : "border-white/10 text-zinc-500 hover:text-zinc-200"
              }`}>
              {t}
            </button>
          ))}
        </div>

        {tab === "Overview" && (
          <div className="space-y-5" data-testid="wc-panel-overview">
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
              <StatCard label="Invested" value={inr(metrics.total_invested)} testId="wc-invested" />
              <StatCard label="Current Value" value={inr(metrics.current_value)} testId="wc-current" />
              <StatCard label="Gain / Loss" value={inr(metrics.gain_loss)} delta={metrics.gain_loss_pct} testId="wc-gain" />
              <StatCard label="CAGR" value={metrics.cagr !== null ? pct(metrics.cagr) : "—"} testId="wc-cagr" />
              <StatCard label="XIRR" value={metrics.xirr !== null ? pct(metrics.xirr) : "—"} testId="wc-xirr" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="glass-card p-6">
                <h3 className="font-heading font-semibold text-white mb-5">Client Profile</h3>
                <div className="grid grid-cols-2 gap-5">
                  <MaskedField label="Email" masked={profile.email} revealed={contact?.email}
                    onReveal={revealContact} revealing={revealing} testId="wc-email" />
                  <MaskedField label="Mobile" masked={profile.mobile} revealed={contact?.mobile}
                    onReveal={revealContact} revealing={revealing} testId="wc-mobile" />
                  <Field label="Onboarded" value={fmtDate(profile.onboarding_date)} />
                  <Field label="Nominee" value={profile.nominee?.name ? `${profile.nominee.name} (${profile.nominee.relation})` : null} />
                </div>
                {profile.contact_masked && !contact && (
                  <p className="text-[10px] text-zinc-600 mt-4 flex items-center gap-1.5" data-testid="wc-contact-masked-note">
                    Contact details are masked for privacy. Revealing them is recorded in the audit log and auto-hides after 60 seconds.
                  </p>
                )}
                {contact && (
                  <p className="text-[10px] text-emerald-500/80 mt-4" data-testid="wc-contact-revealed-note">
                    Contact details visible — auto-hides in 60 seconds. This access was audit logged.
                  </p>
                )}
              </div>
              <div className="glass-card p-6">
                <h3 className="font-heading font-semibold text-white mb-4">Allocation</h3>
                <AllocationDonut data={allocation} height={240} testId="wc-allocation-chart" />
              </div>
            </div>
            <WithdrawalPanel w={withdrawal} testId="wc-withdrawal" />
          </div>
        )}

        {tab === "KYC" && (
          <div className="glass-card p-6 max-w-2xl" data-testid="wc-panel-kyc">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <h3 className="font-heading font-semibold text-white">KYC Records</h3>
              <Badge variant="outline" className={`text-[10px] ${kycBadge(profile.kyc_status)}`}>{profile.kyc_status}</Badge>
              <span className="tnum text-xs text-emerald-300 font-semibold ml-auto" data-testid="wc-kyc-completion">{kyc_completion ?? 0}% complete</span>
            </div>
            <div className="space-y-2.5" data-testid="wc-kyc-records">
              {kyc_records.map((k) => (
                <div key={k.id} className="flex items-center justify-between border border-white/[0.06] bg-white/[0.02] rounded-lg px-4 py-2.5">
                  <span className="text-sm font-medium text-zinc-200">{k.document_type}</span>
                  <Badge variant="outline" className={`text-[10px] ${kycBadge(k.status)}`}>{k.status}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "Documents" && (
          <div className="glass-card overflow-hidden" data-testid="wc-panel-documents">
            {documents.length === 0 ? <EmptyState message="No documents available." /> : documents.map((d) => (
              <div key={d.id} className="px-6 py-3.5 border-b border-white/[0.04] last:border-0 flex items-center gap-4" data-testid={`wc-doc-row-${d.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-white truncate">{d.name}</p>
                  <p className="text-[11px] text-zinc-500">{d.category} · {fmtDate(d.uploaded_at)}</p>
                </div>
                <button onClick={() => downloadDoc(d)} data-testid={`wc-doc-download-${d.id}`}
                  className="h-8 w-8 rounded-lg border border-white/10 flex items-center justify-center text-zinc-400 hover:text-emerald-300 hover:border-emerald-500/30 transition-colors duration-200">
                  <Download size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "Plan & Risk" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="wc-panel-plan">
            <div className="glass-card p-6">
              <h3 className="font-heading font-semibold text-white mb-5">Investment Plan</h3>
              <div className="grid grid-cols-2 gap-5">
                <Field label="Investor Type" value={profile.investor_type} />
                <Field label="Series" value={profile.plan_series ? `${profile.plan_series} Series` : null} />
                <Field label="Plan" value={profile.investment_plan} />
                <Field label="Tenure" value={profile.tenure_category || profile.investment_tenure} />
                <Field label="Indicative Range" value={INVESTOR_TYPES[profile.investor_type]?.range} />
                <Field label="Objective" value={profile.investment_objective} />
              </div>
            </div>
            <div className="glass-card p-6">
              <h3 className="font-heading font-semibold text-white mb-5">Risk Profile</h3>
              <Badge variant="outline" className={`${riskBadge(profile.risk_profile)} text-xs px-3 py-1`}>{profile.risk_profile}</Badge>
              <p className="text-sm text-zinc-500 mt-4 leading-relaxed">
                Core financial, KYC and plan records are maintained by KMC Admin. Contact operations to request changes.
              </p>
            </div>
          </div>
        )}

        {tab === "Notes" && (
          <div className="glass-card p-6" data-testid="wc-panel-notes">
            <h3 className="font-heading font-semibold text-white mb-5">Client Notes</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[["Internal Notes", profile.notes?.internal], ["Client Preferences", profile.notes?.preferences],
                ["Operational Notes", profile.notes?.operational], ["Special Instructions", profile.notes?.special_instructions]].map(([label, val]) => (
                <div key={label} className="border border-white/[0.06] bg-white/[0.02] rounded-lg p-4">
                  <p className="label-caps mb-2">{label}</p>
                  <p className="text-sm text-zinc-300 leading-relaxed">{val || "—"}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
